import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { publicApiBase, developerPath } from '../../utils/developerHost';

const sectionSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  p: { xs: 2.5, md: 3 },
  mb: 2.5,
};

const DeveloperDocs = () => {
  const { t } = useTranslation();
  const apiBase = publicApiBase();

  return (
    <Box>
      <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 34 }, mb: 0.75 }}>
        {t('developerPortal.docs.title')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, mb: 3, maxWidth: '68ch' }}>
        {t('developerPortal.docs.subtitle', { apiBase })}
      </Typography>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.docs.sandboxCardTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '62ch' }}>
          {t('developerPortal.docs.sandboxCardBody')}
        </Typography>
        <Button
          component={RouterLink}
          to={developerPath('sandbox')}
          variant="contained"
          disableElevation
          sx={{ borderRadius: 0, textTransform: 'none', bgcolor: DOCUMENT.oxblood, '&:hover': { bgcolor: DOCUMENT.oxblood } }}
        >
          {t('developerPortal.docs.sandboxCardCta')}
        </Button>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.docs.apiRefCardTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '62ch' }}>
          {t('developerPortal.docs.apiRefCardBody')}
        </Typography>
        <Button
          component={RouterLink}
          to={developerPath('docs/api')}
          variant="contained"
          disableElevation
          sx={{ borderRadius: 0, textTransform: 'none', bgcolor: DOCUMENT.oxblood, '&:hover': { bgcolor: DOCUMENT.oxblood } }}
        >
          {t('developerPortal.docs.apiRefCardCta')}
        </Button>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.docs.quickstart')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 1 }}>
          {t('developerPortal.docs.quickstartBody')}
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.7 }}>
          <li>{t('developerPortal.docs.quickstartStep1')}</li>
          <li>{t('developerPortal.docs.quickstartStep2')}</li>
          <li>{t('developerPortal.docs.quickstartStep3')}</li>
        </Box>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.docs.customCardsTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '62ch' }}>
          {t('developerPortal.docs.customCardsBody')}
        </Typography>
        <Button
          component={RouterLink}
          to={developerPath('docs/api/packages/custom-cards')}
          variant="contained"
          disableElevation
          sx={{ borderRadius: 0, textTransform: 'none', bgcolor: DOCUMENT.oxblood, '&:hover': { bgcolor: DOCUMENT.oxblood } }}
        >
          {t('developerPortal.docs.customCardsCta')}
        </Button>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.docs.embed')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '62ch' }}>
          {t('developerPortal.docs.embedBody')}
        </Typography>
        <Button
          component={RouterLink}
          to={developerPath('widgets')}
          variant="contained"
          disableElevation
          sx={{ borderRadius: 0, textTransform: 'none', bgcolor: DOCUMENT.oxblood, '&:hover': { bgcolor: DOCUMENT.oxblood } }}
        >
          {t('developerPortal.docs.widgetsCta')}
        </Button>
      </Paper>

      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14 }}>
        {t('developerPortal.docs.openapiHint')}{' '}
        <Link href={`${apiBase}/openapi/v1.yaml`} target="_blank" rel="noopener noreferrer" sx={{ color: DOCUMENT.oxblood }}>
          OpenAPI
        </Link>
      </Typography>
    </Box>
  );
};

export default DeveloperDocs;
